import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { TrackingModal } from '@/components/order/TrackingModal';
import { AdminOrderDetail } from './AdminOrderDetail';
import { AdminHero, SearchBox, Count, Avatar } from '@/components/admin/ui';

interface AOrder {
  id: string; reference: string; productTitle: string; buyerName: string; sellerName: string;
  amountGBP: number; totalGBP: number; state: string; deliveryService?: string; trackingCode?: string;
  paidAt?: string; createdAt: string;
}

const DELIVERY: Record<string, string> = { locker: 'InPost Locker', standard: 'Royal Mail', express: 'DPD Express' };

function stateChip(state: string): { label: string; pill: string; dot: string } {
  switch (state) {
    case 'RELEASED':      return { label: 'Released',      pill: 'pill-ok',   dot: 'bg-ok' };
    case 'COLLECTED':     return { label: 'Collected',     pill: 'pill-sage', dot: 'bg-sage' };
    case 'IN_LOCKER':     return { label: 'In locker',     pill: 'pill-sage', dot: 'bg-sage' };
    case 'SHIPPED':       return { label: 'Shipped',       pill: 'pill-sage', dot: 'bg-sage' };
    case 'AWAITING_DROP': return { label: 'Awaiting drop', pill: 'pill-warn', dot: 'bg-warn' };
    case 'PAID_HELD':     return { label: 'In escrow',     pill: 'pill-gold', dot: 'bg-gold' };
    case 'DISPUTED':      return { label: 'Disputed',      pill: 'pill-bad',  dot: 'bg-bad' };
    case 'REFUNDED':      return { label: 'Refunded',      pill: 'pill-clay', dot: 'bg-clay' };
    case 'CANCELLED':     return { label: 'Cancelled',     pill: 'bg-cream-deep text-mute', dot: 'bg-mute' };
    default:              return { label: 'Pending',       pill: 'bg-cream-deep text-mute', dot: 'bg-mute' };
  }
}

const COLS = 'grid grid-cols-[1.5fr_1.4fr_1.1fr_0.8fr_1fr_0.8fr] gap-3 items-center';

export default function AdminOrders() {
  const [q, setQ] = useState('');
  const [state, setState] = useState('');
  const [track, setTrack] = useState<AOrder | null>(null);
  const [manage, setManage] = useState<AOrder | null>(null);
  const { data: rows = [], isLoading } = useQuery<AOrder[]>({
    queryKey: ['admin', 'orders', state],
    queryFn: async () => (await api.get('/admin/orders', { params: { state: state || undefined } })).data,
  });
  const filtered = rows.filter((o) => !q || `${o.reference} ${o.productTitle} ${o.buyerName} ${o.sellerName}`.toLowerCase().includes(q.toLowerCase()));

  const inEscrow = rows.filter((o) => ['CREATED', 'PAID_HELD', 'AWAITING_DROP'].includes(o.state)).length;
  const disputed = rows.filter((o) => o.state === 'DISPUTED').length;

  return (
    <div className="p-6 md:p-10 space-y-6">
      <AdminHero
        eyebrow="Admin · Fulfilment"
        title="Orders & delivery"
        subtitle="Every order across the platform, its escrow state and delivery route."
        stats={[
          { label: 'In view', value: filtered.length },
          { label: 'In escrow', value: inEscrow, accent: 'gold' },
          { label: 'Disputed', value: disputed, accent: disputed ? 'bad' : undefined },
        ]}
      />

      <div className="flex flex-wrap items-center gap-2">
        <SearchBox value={q} onChange={setQ} placeholder="Search ref, item, buyer, seller…" className="w-80" />
        <select value={state} onChange={(e) => setState(e.target.value)} className="input w-auto text-sm">
          <option value="">All states</option>
          {['PAID_HELD', 'AWAITING_DROP', 'IN_LOCKER', 'COLLECTED', 'DISPUTED', 'RELEASED', 'REFUNDED', 'CANCELLED'].map((s) => (
            <option key={s} value={s}>{s.replace(/_/g, ' ').toLowerCase()}</option>
          ))}
        </select>
        <Count n={filtered.length} noun="order" />
      </div>

      <div className="card overflow-x-auto">
        <div className={`${COLS} px-5 py-3 mono text-mute text-[9px] tracking-[0.14em] uppercase border-b border-warmline bg-cream-deep min-w-[900px]`}>
          <div>Order</div><div>Parties</div><div>Delivery</div><div>Total</div><div>State</div><div className="text-right">Manage</div>
        </div>

        {isLoading ? (
          [...Array(6)].map((_, i) => (
            <div key={i} className={`${COLS} px-5 py-3.5 border-b border-warmline last:border-0 min-w-[900px]`}>
              <div className="space-y-1.5"><div className="h-2.5 w-16 bg-cream-deep rounded animate-pulse" /><div className="h-3 w-40 bg-cream-deep rounded animate-pulse" /></div>
              <div className="flex items-center gap-2"><div className="w-7 h-7 rounded-full bg-cream-deep animate-pulse" /><div className="h-3 w-24 bg-cream-deep rounded animate-pulse" /></div>
              <div className="h-3 w-20 bg-cream-deep rounded animate-pulse" />
              <div className="h-3 w-12 bg-cream-deep rounded animate-pulse" />
              <div className="h-5 w-20 bg-cream-deep rounded-full animate-pulse" />
              <div />
            </div>
          ))
        ) : filtered.map((o) => {
          const s = stateChip(o.state);
          return (
            <div key={o.id} className={`${COLS} px-5 py-3.5 text-sm border-b border-warmline last:border-0 min-w-[900px] transition-colors hover:bg-cream-deep/50`}>
              <div className="min-w-0"><div className="text-[11px] text-mute mono">{o.reference}</div><div className="truncate font-medium">{o.productTitle}</div></div>
              <div className="min-w-0 flex items-center gap-2.5">
                <Avatar name={o.buyerName} tone="sage" className="w-7 h-7 text-[10px]" />
                <div className="min-w-0 text-[12px] leading-tight">
                  <div className="truncate">{o.buyerName}</div>
                  <div className="truncate text-mute">→ {o.sellerName}</div>
                </div>
              </div>
              <div className="text-[12px]">{DELIVERY[o.deliveryService ?? ''] ?? o.deliveryService ?? '-'}{o.trackingCode && <div className="text-[10px] text-mute mono truncate">{o.trackingCode}</div>}</div>
              <div className="display text-base">£{o.totalGBP.toFixed(2)}</div>
              <div><span className={`pill text-[10px] ${s.pill}`}><span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />{s.label}</span></div>
              <div className="text-right flex flex-col items-end gap-0.5">
                <button onClick={() => setManage(o)} className="text-[11px] text-gold-dark hover:underline font-medium">Manage</button>
                <button onClick={() => setTrack(o)} className="text-[11px] text-mute hover:text-ink">Track</button>
              </div>
            </div>
          );
        })}
        {!isLoading && !filtered.length && (
          <div className="px-5 py-16 text-center">
            <div className="text-3xl mb-2 opacity-60">📦</div>
            <div className="display text-lg">No orders found</div>
            <p className="text-mute text-sm mt-1">{q ? `Nothing matches “${q}”.` : 'Try a different state filter.'}</p>
          </div>
        )}
      </div>

      {track && <TrackingModal orderId={track.id} title={track.productTitle} onClose={() => setTrack(null)} />}
      {manage && <AdminOrderDetail orderId={manage.id} onClose={() => setManage(null)} />}
    </div>
  );
}
